import { FitnessPlan, Plan_Workout, planIsBuiltButHasNoExercises } from '@cf-shared/data-model/fitness-plan';

// #3320 Phase 1: canonical "filter id out of workout.subSlotIds, then drop the now-unreferenced
// plan.subSlots entry" sequence. One call site needs this: edit-workouts-page
// .removeExerciseFromWorkout (which previously only filtered the workout array, leaving orphan
// dict entries — root cause of #3320). A second one used to exist, the action === 'remove' branch
// of exercise-switching.switchExerciseAsync_t; nothing ever called it with that action, and it was
// deleted when that function was split into an add and a swap (df-c9ee6db39869).
//
// Multi-use detection mirrors the existing pattern at exercise-switching.ts (numUses > 0
// after filter means another workout still references the id).
export function removeSubslotFromWorkout(
  plan: FitnessPlan,
  workout: Plan_Workout,
  subSlotId: string,
): void {
  workout.subSlotIds = workout.subSlotIds.filter(id => id !== subSlotId);

  let numUses = 0;
  for (const w of plan.workouts) {
    if (w.subSlotIds?.includes(subSlotId)) numUses++;
  }
  if (numUses === 0) {
    delete plan.subSlots[subSlotId];
  }
}

// Edit Workouts' "Move Exercise To": take the subslot out of one workout and append it to
// another. The dictionary entry is untouched, because the subslot is still referenced by the
// target workout. A target that already lists the subslot (it shares the exercise's progression
// with the source, #4004) keeps it once: pushing it again left a duplicate id that the save then
// reported as plan.duplicate-subslotid-at-save (#6296).
export function moveSubslotToWorkout(
  fromWorkout: Plan_Workout,
  toWorkout: Plan_Workout,
  subSlotId: string,
): void {
  // remove from current workout
  fromWorkout.subSlotIds = fromWorkout.subSlotIds.filter(id => id !== subSlotId);

  // add to target workout (at the end), unless it is already there
  if (!toWorkout.subSlotIds.includes(subSlotId)) {
    toWorkout.subSlotIds.push(subSlotId);
  }

  // clear combo set grouping for moved exercise
  if (fromWorkout.comboSetGroups) {
    delete fromWorkout.comboSetGroups[subSlotId];
  }
}

// #3320 Phase 1: garbage-collect orphan plan.subSlots entries (dict entries unreferenced by
// any workout). Symmetric counterpart of user-db.service's "fixup missing subslots in
// workouts" loop, which handles the reverse direction (workout-array id → missing dict).
//
// Returns the list of deleted ids so the caller can mark needsSaving. The helper itself is
// telemetry-free; the call site decides whether to alert. Two call sites:
//   - user-db.service.loadPlanAsync — silent heal of latent pre-#3320 data on first boot.
//   - user-db.service.savePlanAsync — invariant canary; fires 'plan.orphan-subslot-at-save'
//     when a write path hands an orphan-bearing plan to the save (post-#3320 regression).
export function gcOrphanSubslots(plan: FitnessPlan): string[] {
  // if either side of the round-trip is missing, nothing to compare
  if (!plan.workouts || !plan.subSlots) return [];
  // #4587: a plan that was already built, but whose training days now reference no
  // exercise at all, is damage rather than an untidy plan. Every dictionary entry looks
  // unreferenced, so collecting them here would delete the user's whole programme — and
  // it did, silently, to a real customer. Leave the exercises where they are; the caller
  // recovers the plan from a backup instead (userDb.loadPlanAsync).
  if (planIsBuiltButHasNoExercises(plan)) return [];
  const referencedIds = new Set<string>();
  for (const workout of plan.workouts) {
    if (!workout.subSlotIds) continue;
    for (const id of workout.subSlotIds) referencedIds.add(id);
  }
  const orphanIds: string[] = [];
  for (const id of Object.keys(plan.subSlots)) {
    if (!referencedIds.has(id)) orphanIds.push(id);
  }
  if (orphanIds.length === 0) return [];
  for (const id of orphanIds) delete plan.subSlots[id];
  return orphanIds;
}
